import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '../../GeneralComponents/Container';
import { Topbar, Sidebar } from '../components';
import setGlobalLoader from '../../../redux/actions/loaderActions';
import { updateBatch } from '../../../redux/actions/apiActions';

const validationSchema = yup.object({
    name: yup.string().required('Batch name is required.').trim(),
    notes: yup.string().trim(),
});

const EditBatch = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const { state } = useLocation();
    const batchData = state?.batchData;

    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });
    const [openSidebar, setOpenSidebar] = useState(false);
    const open = isMd ? false : openSidebar;

    const handleOnSubmit = async (values) => {
        dispatch(setGlobalLoader(true));
        try {
            await updateBatch(params.id, values);
            toast.success('Batch updated successfully');
            navigate('/batches');
        } catch (error) {
            console.log('update batch error', error);
            toast.error(error?.response?.data?.message ?? 'Error updating batch');
        } finally {
            dispatch(setGlobalLoader(false));
        }
    };

    if (!batchData) {
        return (
            <Box>
                <AppBar position="fixed" sx={{ backgroundColor: theme.palette.background.paper, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }} elevation={0}>
                    <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                        <Topbar onSidebarOpen={() => setOpenSidebar(true)} />
                    </Container>
                </AppBar>
                <Sidebar onClose={() => setOpenSidebar(false)} open={open} variant={isMd ? 'permanent' : 'temporary'} />
                <main>
                    <Box height={{ xs: 94, sm: 102, md: 107 }} />
                    <Box overflow="hidden" marginLeft={{ md: '270px' }} p={3}>
                        <Typography variant="h6">No batch data found</Typography>
                        <Button variant="contained" onClick={() => navigate('/batches')} sx={{ mt: 2 }}>Go Back</Button>
                    </Box>
                </main>
            </Box>
        );
    }

    return (
        <Box>
            <AppBar
                position="fixed"
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
                elevation={0}
            >
                <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                    <Topbar onSidebarOpen={() => setOpenSidebar(true)} />
                </Container>
            </AppBar>
            <Sidebar
                onClose={() => setOpenSidebar(false)}
                open={open}
                variant={isMd ? 'permanent' : 'temporary'}
            />
            <main>
                <Box height={{ xs: 94, sm: 102, md: 107 }} />
                <br />
                <Box overflow="hidden" marginLeft={{ md: '270px' }} p={3}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ marginBottom: 2 }}>
                        Edit Batch
                    </Typography>
                    <Formik
                        initialValues={{
                            name: batchData?.name || '',
                            notes: batchData?.notes || '',
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleOnSubmit}
                        enableReinitialize
                    >
                        {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting }) => (
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={{ xs: 2, md: 4 }}>
                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                            Batch Name *
                                        </Typography>
                                        <TextField
                                            variant="outlined"
                                            name="name"
                                            fullWidth
                                            value={values.name}
                                            onChange={(e) => setFieldValue('name', e.target.value)}
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                                            Notes
                                        </Typography>
                                        <TextField
                                            variant="outlined"
                                            name="notes"
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={values.notes}
                                            onChange={(e) => setFieldValue('notes', e.target.value)}
                                            error={touched.notes && Boolean(errors.notes)}
                                            helperText={touched.notes && errors.notes}
                                        />
                                    </Grid>
                                </Grid>
                                <Box mt={3}>
                                    <Button variant="contained" type="submit" disabled={isSubmitting}>
                                        {isSubmitting ? <CircularProgress size={24} /> : 'Update'}
                                    </Button>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Box>
            </main>
        </Box>
    );
};

export default EditBatch;
